import { getEnvStoryQueueId } from "@caas/getEnvStoryQueueId";
import EventsAwardsDTALogoMenu from "@dta/components/EventsAwardsDTALogoMenu";
import EventsAwardsE50LogoMenu from "@e50/components/EventsAwardsE50LogoMenu";
import EventsAwardsEELogoMenu from "@ee/components/EventsAwardsEELogoMenu";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import { AwardType, Winner } from "@events-awards/utils/types";
import EventsAwardsSBALogoMenu from "@sba/components/EventsAwardsSBALogoMenu";
import EventsAwardsSCALogoMenu from "@sca/components/EventsAwardsSCALogoMenu";
import AssesmentSection from "@sia/components/AssesmentSection";
import DeclarationSection from "@sia/components/DeclarationSection";
import EventsAwardsSIALogoMenu from "@sia/components/EventsAwardsSIALogoMenu";
import NominationsSection from "@sia/components/NominationsSection";
import SIAAboutPage from "@sia/components/SIAAboutPage";
import SIAAwardsPage from "@sia/components/SIAAwardsPage";
import SIAContactUsPage from "@sia/components/SIAContactUsPage";
import SIAFooter from "@sia/components/SIAFooter";
import SIAHowToNominatePage from "@sia/components/SIAHowToNominatePage";
import SIAJudgesPage from "@sia/components/SIAJudgesPage";
import SIAPartnersPage from "@sia/components/SIAPartnersPage";
import type { Element, SanitizedFeedSectionArticle } from "@typings/Cue";
import { ENVIRONMENT } from "@util/constant";
import dayjs from "dayjs";

export const getAwardType = (
  sectionUniqueName: string
): AwardType | undefined => {
  switch (sectionUniqueName) {
    case "events-awards_enterprise-50":
      return AwardType.E50;
    case "events-awards_emerging-enterprise":
      return AwardType.EE;
    case "events-awards_singapore-business-awards":
      return AwardType.SBA;
    case "events-awards_singapore-corporate-awards":
      return AwardType.SCA;
    case "events-awards_sustainability-impact-awards":
      return AwardType.SIA;
    case "events-awards_design-ai-and-tech-awards":
      return AwardType.DATA;
  }
};

export const EventsAwardsLayoutFactory = (awardType: AwardType) => {
  switch (awardType) {
    case AwardType.SBA:
      return EventsAwardsSBALogoMenu;
    case AwardType.SCA:
      return EventsAwardsSCALogoMenu;
    case AwardType.SIA:
      return EventsAwardsSIALogoMenu;
    case AwardType.E50:
      return EventsAwardsE50LogoMenu;
    case AwardType.EE:
      return EventsAwardsEELogoMenu;
    case AwardType.DATA:
      return EventsAwardsDTALogoMenu;
  }
};

export const EventsAwardsFooterFactory = (awardType: AwardType) => {
  switch (awardType) {
    case AwardType.SIA:
      return SIAFooter;
  }
};

/**
 * Helper function to change all dash to
 * space and capitalize every word
 * @param text
 * @returns toEventsAwardsMicrositeTitleCase
 */
export function toEventsAwardsMicrositeTitleCase(text: string) {
  return text
    .replace("-", " ") // Replace hyphen with space
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize the first letter of each word
}

export const eventsAwardsMainPageStoryQueueId = (
  awardType: AwardType
): string => {
  switch (awardType) {
    case AwardType.SBA:
      return getEnvStoryQueueId("1295782", "1295782", "1295782", ENVIRONMENT);
    case AwardType.SCA:
      return getEnvStoryQueueId("1295790", "1295790", "1295790", ENVIRONMENT);
    case AwardType.SIA:
      return getEnvStoryQueueId("1295781", "1295781", "1295781", ENVIRONMENT);
    case AwardType.E50:
      return getEnvStoryQueueId("1295479", "1295479", "1295479", ENVIRONMENT);
    case AwardType.EE:
      return getEnvStoryQueueId("1295783", "1295783", "1295783", ENVIRONMENT);
    case AwardType.DATA:
      return "";
  }
};

// Utility: Get Award Data
export const getAwardData = (awardType: AwardType) => {
  return AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
};

export const processArticleElements = (elements: Element[]): Winner[] => {
  const awards: Winner[] = [];
  let currentAward: Winner = {};

  elements.forEach((element) => {
    if (element.type === "paragraph") {
      const value = element.fields[0]?.value;
      const isBold = element.fields[0]?.annotations?.some(
        (a) => a.name === "bold" && a.value === "true"
      );

      if (isBold) {
        // If we have a previous award, push it to awards array
        if (currentAward.title) {
          awards.push({ ...currentAward });
        }
        // Start a new award
        currentAward = { title: value, type: "paragraph" };
      } else {
        // Add details to current award
        if (!currentAward.recipient) {
          currentAward.recipient = value;
        } else if (!currentAward.position) {
          currentAward.position = value;
        } else if (!currentAward.organization) {
          currentAward.organization = value;
        }
      }
    }
    if (element.type === "table") {
      currentAward.recipient = element.fields[0]?.value;
      currentAward.type = "table";
    }
  });

  // Push the last award
  if (currentAward.title) {
    awards.push(currentAward);
  }

  return awards;
};

export type Image = Element & {
  type: "image";
  relation: {
    fields: {
      "landscape-caas": {
        url: string;
        width: number;
        height: number;
      };
    };
  };
};

export const isImageElement = (element: Element): element is Image => {
  return (
    element.type === "image" &&
    element.relation?.fields?.["landscape-caas"]?.url !== undefined
  );
};

export const getAllImages = (
  articles: SanitizedFeedSectionArticle[] = []
): Image[] => {
  return (
    articles?.flatMap(
      (article) => article.elements?.filter(isImageElement) ?? []
    ) ?? []
  );
};

/**
 * Maps an award slug to its corresponding AwardType enum value.
 * This function is used to convert URL friendly award names to their internal enum representation.
 *
 * @param awards - The URL friendly award name (e.g., 'enterprise-50', 'singapore-business-awards')
 * @returns The corresponding AwardType enum value, or null if no matching award type is found
 *
 * @example
 * mapAwardType('enterprise-50') // returns AwardType.E50
 * mapAwardType('unknown-award') // returns null
 */
export const mapAwardType = (awards: string): AwardType | null => {
  const awardTypeMap: Record<string, AwardType> = {
    "singapore-corporate-awards": AwardType.SCA,
    "enterprise-50": AwardType.E50,
    "emerging-enterprise": AwardType.EE,
    "singapore-business-awards": AwardType.SBA,
    "sustainability-impact-awards": AwardType.SIA,
    "design-ai-and-tech-awards": AwardType.DATA,
  };
  return awardTypeMap[awards] || null;
};

/**
 * Determines if a menu link should be marked as active based on the current URL.
 *
 * - Trims trailing slashes from both `menuLink` and `pathname` for comparison.
 * - Returns true if:
 *   - The current path matches the menu link exactly, or
 *   - The current path starts with the menu link and includes `/gallery/` (to handle nested routes like galleries).
 *
 * @param menuLink - The absolute path of the menu link/component (e.g., "/events").
 * @param pathname - The current URL path from the window location (e.g., "/events/gallery/2025").
 * @returns A boolean indicating whether the link is active.
 */
export const isActiveLink = (menuLink: string, pathname: string) => {
  const currentPath = pathname.replace(/\/$/, "");
  const baseMenuLink = menuLink.replace(/\/$/, "");

  return (
    currentPath === baseMenuLink ||
    (currentPath.startsWith(baseMenuLink) && currentPath.includes("/gallery/"))
  );
};

/**
 * Returns the corresponding static component to render based on the provided page identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (SIA) pages like "about", "awards", or "how-to-nominate".
 *
 * @param page - The string identifier of the page (e.g., "about", "awards", "how-to-nominate").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const renderSIAStaticComponents = (page: string) => {
  switch (page) {
    case "about":
      return SIAAboutPage;
    case "awards":
      return SIAAwardsPage;
    case "how-to-nominate":
      return SIAHowToNominatePage;
    case "contact-us":
      return SIAContactUsPage;
    case "partners":
      return SIAPartnersPage;
    case "judges":
      return SIAJudgesPage;
  }
};

/**
 * Returns the corresponding static component to render based on the provided active tab identifier.
 *
 * This function is typically used to dynamically render different sections of the
 * Sustainability Impact Awards (SIA) How to Nominate page.
 *
 * @param page - The string identifier of the active tab (e.g., "nominations", "assessment", "declarations").
 * @returns The React component corresponding to the given page, or `undefined` if not matched.
 */
export const renderSIAHowToNominateComponents = (activeTab: string) => {
  switch (activeTab) {
    case "nominations":
      return NominationsSection;
    case "assessment":
      return AssesmentSection;
    case "declarations":
      return DeclarationSection;
  }
};

/**
 * Returns the corresponding formatted date for Header.
 *
 * This function is typically used to format the todays date in a readable format for
 * Sustainability Impact Awards (SIA) Header.
 *
 * @returns The formatted date string in 'EEE, dd MMM yyyy' format.
 */
export const formatDateToReadable = () => {
  const date = dayjs();

  return date.format("ddd, MMM DD, YYYY");
};
